import React from 'react'
import PropTypes from 'prop-types'
import useSWR from 'swr'
import {
  Box,
  CloseButton,
  Text,
  Drawer,
  DrawerOverlay,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  Flex,
  Divider,
  Link,
  Tag,
  TagLabel,
  TagLeftIcon,
} from '@chakra-ui/react'
import {t} from 'i18next'
import {get} from 'lodash-es'
import dayjs from 'dayjs'
import pluralize from 'pluralize'

import {CopyButton, Skeleton, Input} from '../../components'
import CancelCashIn from './CancelCashIn'
import InvoiceTimeline from './InvoiceTimeline'
import ReportDirectPayment from './ReportDirectPayment'
import OverdueDetails from './OverdueDetails'
import useFeatureFlags from '../../hooks/useFeatureFlags'
import AllocateCreditNotePanel from '../../components/AllocateCreditNotePanel'
import {useRails} from '../../contexts/rails'
import {camelizeResult, dineroMoneyObjects} from '../../../api/serializer'
import {toEpochMillisecond, toLocalDate} from '../../../utils/formatter'
import {CoinCashInIcon, SmallInverseTickedIcon} from '../../icons'

const DATE_FORMAT = {day: 'numeric', month: 'short', year: 'numeric'}

const paymentStatusColorScheme = (status) => {
  if (status === 'Paid') return 'success'
  if (status === 'Partially Paid') return 'neutral'
  return 'info'
}

const SectionLabel = ({children}) => (
  <Text textStyle="headline4" textTransform="uppercase" color="mld.neutral.900">
    {children}
  </Text>
)

SectionLabel.propTypes = {children: PropTypes.node.isRequired}

const SectionRow = ({label, children}) => (
  <Flex justify="space-between" align="center" gap="sm">
    <SectionLabel>{label}</SectionLabel>
    {children}
  </Flex>
)

SectionRow.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
}

const DetailRow = ({label, children}) => (
  <Flex justify="space-between" align="center" gap="sm">
    <Text
      textStyle="body-small"
      textTransform="uppercase"
      color="mld.neutral.800"
    >
      {label}
    </Text>
    <Box textAlign="right">{children}</Box>
  </Flex>
)

DetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
}

const DetailValue = ({children}) => (
  <Text textStyle="body-intro" color="mld.neutral.900">
    {children}
  </Text>
)

DetailValue.defaultProps = {children: null}
DetailValue.propTypes = {children: PropTypes.node}

const InvoiceDrawer = ({isOpen, onClose, invoice, mutateList}) => {
  const paymentLink = `${window.location.origin}/pay/${invoice.xeroInvoiceUuid}`
  const {organisation} = useRails()

  // Chakra locks scrolling on `body`, but the supplier portal scrolls on the
  // document element, so on short viewports its scrollbar stays visible behind
  // the drawer alongside the drawer's own. Lock the document while open.
  React.useEffect(() => {
    if (!isOpen) return undefined

    const {style} = document.documentElement
    const previousOverflow = style.overflow
    style.overflow = 'hidden'

    return () => {
      style.overflow = previousOverflow
    }
  }, [isOpen])

  const {
    data: {features},
    loading: loadingFeatures,
  } = useFeatureFlags()

  const overdueInvoiceInfoEnabled = features && features.overdueInvoiceInfo
  const creditNotesEnabled = features && features.creditNotes

  const {data: detailData, isLoading: detailLoading} = useSWR(
    `/api/suppliers/${organisation.currentSupplier.id}/invoices/${invoice.id}`,
    {use: [camelizeResult, dineroMoneyObjects]},
  )
  const {invoice: detail = {}} = detailData || {}

  const {data, error, mutate} = useSWR(`/api/invoices/${invoice.id}/timeline`)
  const loading = (!data && !error) || loadingFeatures

  const timeline = get(data, 'timeline')
  const canCancelCashIn = get(data, 'can_cancel_cash_in')
  const canReportDirectPayment = get(data, 'can_report_direct_payment')
  const overdueMessage = get(data, 'overdue_message')
  const cashedIn = get(data, 'cashed_in')

  const onInvoicesPage = window.location.toString().match(/\/invoices/)
  const paymentTerm =
    detail.issueDate && detail.dueDate
      ? dayjs(toEpochMillisecond(detail.dueDate)).diff(
          toEpochMillisecond(detail.issueDate),
          'day',
        )
      : null

  return (
    <Drawer isOpen={isOpen} placement="right" size="md" onClose={onClose}>
      <DrawerOverlay />

      <DrawerContent>
        <DrawerHeader>
          <Flex align="center" justify="space-between" gap="sm" py="xs">
            <Flex align="center" gap="sm">
              <Text textStyle="headline3">{invoice.number}</Text>
              {invoice.paymentStatus && (
                <Tag
                  variant="status"
                  colorScheme={paymentStatusColorScheme(invoice.paymentStatus)}
                >
                  <TagLeftIcon boxSize="12px" mr="1" as={CoinCashInIcon} />
                  <TagLabel>{invoice.paymentStatus}</TagLabel>
                </Tag>
              )}
            </Flex>

            <CloseButton onClick={onClose} />
          </Flex>
        </DrawerHeader>

        <DrawerBody overflowY="auto">
          <Flex direction="column" gap="md">
            <Divider borderColor="mld.neutral.500" />

            <SectionRow label={t('invoiceDetails.customerLabel')}>
              <Link
                href={`/customers/${invoice.customerId}`}
                isExternal
                textStyle="body-copy"
                color="mld.neutral.900"
                textDecoration="underline"
                className="fs-mask"
              >
                {invoice.contactName}
              </Link>
            </SectionRow>

            <Divider borderColor="mld.neutral.500" />

            <Box>
              <SectionRow label={t('invoiceDetails.invoiceLabel')}>
                <DetailValue>{invoice.total}</DetailValue>
              </SectionRow>

              <Divider borderColor="mld.neutral.500" my="md" />

              <Flex direction="column" gap="md" className="fs-mask">
                <DetailRow label={t('invoiceDetails.invoiceNumberLabel')}>
                  {onInvoicesPage ? (
                    <DetailValue>{invoice.number}</DetailValue>
                  ) : (
                    <Link
                      href={`/invoices?invoice_id=${invoice.id}`}
                      isExternal
                      textStyle="body-intro"
                      color="mld.neutral.900"
                      textDecoration="underline"
                    >
                      {invoice.number}
                    </Link>
                  )}
                </DetailRow>

                <DetailRow label={t('invoiceDetails.issueDateLabel')}>
                  <Skeleton isLoaded={!detailLoading}>
                    <DetailValue>
                      {detail.issueDate &&
                        toLocalDate(
                          toEpochMillisecond(detail.issueDate),
                          DATE_FORMAT,
                        )}
                    </DetailValue>
                  </Skeleton>
                </DetailRow>

                <DetailRow label={t('invoiceDetails.dueDateLabel')}>
                  <Skeleton isLoaded={!detailLoading}>
                    <DetailValue>
                      {detail.dueDate &&
                        toLocalDate(
                          toEpochMillisecond(detail.dueDate),
                          DATE_FORMAT,
                        )}
                    </DetailValue>
                  </Skeleton>
                </DetailRow>

                <DetailRow label={t('invoiceDetails.paymentTermLabel')}>
                  <Skeleton isLoaded={!detailLoading}>
                    <DetailValue>
                      {paymentTerm !== null &&
                        `${paymentTerm} ${pluralize('day', paymentTerm)}`}
                    </DetailValue>
                  </Skeleton>
                </DetailRow>
              </Flex>
            </Box>

            <Divider borderColor="mld.neutral.500" />

            <SectionRow label={t('invoiceDetails.cashinLabel')}>
              {invoice.cashinStatus ? (
                <Tag variant="primary" colorScheme="primary">
                  <TagLeftIcon
                    boxSize="12px"
                    as={SmallInverseTickedIcon}
                    mr="1"
                  />
                  <TagLabel>
                    {t('invoiceDetails.cashinValue', {context: 'true'})}
                  </TagLabel>
                </Tag>
              ) : (
                <Text textStyle="body-small" color="mld.neutral.700">
                  {t('invoiceDetails.cashinValue', {context: 'false'})}
                </Text>
              )}
            </SectionRow>

            {creditNotesEnabled && (
              <>
                <Divider borderColor="mld.neutral.500" />
                <SectionRow label={t('invoiceDetails.creditNoteLabel')}>
                  <AllocateCreditNotePanel
                    invoiceId={invoice.id}
                    onClose={onClose}
                  />
                </SectionRow>
              </>
            )}

            <Divider borderColor="mld.neutral.500" />

            <Box>
              <Text
                textStyle="body-copy-medium"
                color="mld.neutral.800"
                mb="xs"
              >
                {t('invoiceDetails.paymentLinkLabel')}
              </Text>

              <Flex align="center">
                <Input
                  name="payment link"
                  placeholder={t('invoiceDetails.paymentLinkPlaceholder')}
                  value={paymentLink}
                  overflowX="scroll"
                  isReadOnly
                />

                <CopyButton
                  ml="xs"
                  color="mld.neutral.900"
                  label={t('invoiceDetails.copyPaymentLinkLabel')}
                  content={paymentLink}
                />
              </Flex>

              {overdueInvoiceInfoEnabled && invoice.overDue && (
                <OverdueDetails
                  invoice={invoice}
                  closeDrawer={onClose}
                  mutateList={mutateList}
                  overdueMessage={overdueMessage}
                />
              )}

              <ReportDirectPayment
                canReportDirectPayment={canReportDirectPayment}
                cashedIn={cashedIn}
                contactName={invoice.contactName}
                invoiceId={invoice.id}
                invoiceNumber={invoice.number}
                dueAmount={invoice.dueAmount}
                updateInvoiceTimeline={mutate}
              />
            </Box>

            <Divider borderColor="mld.neutral.500" />

            <Skeleton isLoaded={!loading}>
              {error && (
                <Text mb="sm">{t('invoiceDetails.timelineError')}</Text>
              )}

              {canCancelCashIn && (
                <CancelCashIn
                  invoiceNumber={invoice.number}
                  xeroInvoiceUuid={invoice.xeroInvoiceUuid}
                  updateInvoiceTimeline={mutate}
                />
              )}

              <InvoiceTimeline timeline={timeline} />
            </Skeleton>
          </Flex>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  )
}

InvoiceDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  mutateList: PropTypes.func.isRequired,
  invoice: PropTypes.shape({
    id: PropTypes.string.isRequired,
    cashinStatus: PropTypes.bool.isRequired,
    xeroInvoiceUuid: PropTypes.string.isRequired,
    number: PropTypes.string.isRequired,
    contactName: PropTypes.string.isRequired,
    customerId: PropTypes.string.isRequired,
    paymentStatus: PropTypes.string,
    total: PropTypes.string.isRequired,
    dueAmount: PropTypes.string.isRequired,
    overDueDays: PropTypes.number.isRequired,
    overDue: PropTypes.bool.isRequired,
  }).isRequired,
}

export default InvoiceDrawer
